import React from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmployeePreview from "./EmployeePreview";
import { BRAND, kioskInputSx } from "../styles/authTheme";

const sectionShell = {
  borderRadius: "14px",
  bgcolor: "#F8FAFC",
  border: `1px solid ${BRAND.border}`,
  p: { xs: 1.25, sm: 1.75 },
  mb: { xs: 1.25, sm: 2 },
};

const EmployeeSection = ({
  employeeId,
  onEmployeeIdChange,
  employeeName,
  punchStatus,
  checkInTime,
  loadingStatus,
  inputRef,
}) => {
  const hasValidEmployee = Boolean(employeeName);
  const hasInput = Boolean(employeeId.trim());

  return (
    <Box sx={sectionShell}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "10px",
            bgcolor: BRAND.orangeLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BadgeOutlinedIcon sx={{ fontSize: 18, color: BRAND.orange }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: BRAND.navy,
              fontSize: { xs: "0.88rem", sm: "0.95rem" },
              lineHeight: 1.2,
            }}
          >
            Employee ID
          </Typography>
          <Typography
            sx={{
              color: BRAND.muted,
              fontSize: { xs: "0.68rem", sm: "0.75rem" },
              lineHeight: 1.3,
            }}
          >
            Type your ID number to continue
          </Typography>
        </Box>
      </Box>

      <TextField
        placeholder="e.g. EMP001"
        fullWidth
        value={employeeId}
        onChange={(e) => onEmployeeIdChange(e.target.value.toUpperCase())}
        autoComplete="off"
        autoFocus
        variant="outlined"
        inputRef={inputRef}
        inputProps={{ inputMode: "numeric" }}
        sx={{
          ...kioskInputSx,
          mb: hasInput ? { xs: 1.25, sm: 1.5 } : 0,
          "& .MuiOutlinedInput-root": {
            ...kioskInputSx["& .MuiOutlinedInput-root"],
            minHeight: { xs: 52, sm: 58 },
            bgcolor: "#fff",
            fontSize: { xs: "1.2rem", sm: "1.3rem" },
            fontWeight: 700,
            letterSpacing: "0.06em",
            ...(hasValidEmployee
              ? {
                  "& fieldset": { borderColor: BRAND.success, borderWidth: "1.5px" },
                  boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.1)",
                }
              : {}),
          },
          "& .MuiInputLabel-root": { display: "none" },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  letterSpacing: "0.08em",
                  mr: 0.5,
                }}
              >
                ID
              </Typography>
            </InputAdornment>
          ),
          endAdornment: loadingStatus ? (
            <CircularProgress size={22} sx={{ color: BRAND.orange }} />
          ) : hasValidEmployee ? (
            <InputAdornment position="end">
              <CheckCircleIcon sx={{ color: BRAND.success, fontSize: 22 }} />
            </InputAdornment>
          ) : null,
        }}
      />

      {(hasInput || loadingStatus) && (
        <Box
          sx={{
            pt: { xs: 0.25, sm: 0.5 },
            borderTop: hasInput ? `1px dashed ${BRAND.border}` : "none",
          }}
        >
          <EmployeePreview
            embedded
            employeeId={employeeId}
            employeeName={employeeName}
            punchStatus={punchStatus}
            checkInTime={checkInTime}
            loading={loadingStatus}
          />
        </Box>
      )}

      {!hasInput && (
        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            mt: 1,
            textAlign: "center",
            color: BRAND.muted,
            fontSize: "0.78rem",
          }}
        >
          Employee profile appears here after you enter an ID
        </Typography>
      )}
    </Box>
  );
};

export default EmployeeSection;
